<template>
  <span class="reports-nav-wrapper">
    <!-- Trigger: acceso directo a los reportes de GeneralReport con los filtros
    que ya conocemos de la sección preseleccionados. -->
    <b-button
      :id="triggerId"
      size="sm"
      variant="none"
      class="reports-nav-trigger"
      v-b-tooltip.hover.v-secondary.noninteractive="
        $t('dashboard.directiveDashboard.reportsNavigationTooltip')
      "
    >
      <b-icon icon="bar-chart-line-fill"></b-icon>
    </b-button>
    <b-popover :target="triggerId" triggers="hover focus">
      <template #title>{{ $t("toolbar.reports.reportsTitle") }}</template>
      <div class="reports-nav-popover">
        <b-button
          v-for="option in report_general_options"
          :key="option.value"
          variant="primary"
          class="reports-nav-btn"
          @click="navigateToGeneralReport(option)"
          @contextmenu.prevent="navigateToGeneralReport(option, true)"
        >
          <b-icon :icon="option.icon" class="mr-1"></b-icon>
          {{ option.text }}
        </b-button>
      </div>
    </b-popover>
  </span>
</template>

<script>
export default {
  name: "SectionReportsNav",
  props: {
    // Perfil de egreso al que pertenece la sección (filtro egress_profiles).
    egress_profile_id: {
      type: Number,
      required: true,
    },
    // EgressProfileMatter (asignatura del perfil): filtro matters.
    egress_profile_matter_id: {
      type: Number,
      required: true,
    },
    // Sección desde la que se navega: filtro sections.
    section_id: {
      type: Number,
      required: true,
    },
    // Nivel del plan (a través de la asignatura): filtro levels.
    plan_level: {
      type: Number,
      default: null,
    },
    // Período (a través de la sección): filtro periods.
    period_id: {
      type: Number,
      default: null,
    },
  },
  computed: {
    // Id único por instancia para vincular el trigger con su popover.
    triggerId() {
      return `section-reports-nav-${this._uid}`;
    },
    // Reportes navegables desde una sección activa: los mismos 8 que ofrece
    // GeneralReport.vue y en el mismo orden de las tarjetas (3, 2, 1, 4, 5, 6,
    // 8, 7). Cada uno preselecciona solo los filtros que se conocen a nivel de
    // sección: perfil de egreso (siempre), asignatura y sección (según el
    // reporte), el nivel del plan, el período, el estado de estudiante ("todos")
    // y las cohortes/índices ("todos"). No todos los filtros aplican a cada
    // reporte: las banderas controlan qué se pasa. El fallback de cada nombre
    // reutiliza el mismo texto/interpolaciones que GeneralReport.vue.
    //
    // Banderas por reporte:
    //   use_matter / use_section       -> filtros que el reporte realmente usa
    //   use_level / use_period         -> derivados de la asignatura/sección
    //   use_is_active_section          -> período vigente (solo Progresión)
    //   use_student_status             -> "todos" (en régimen + no en régimen)
    //   use_cohorts / use_cohort_index -> "todos" (todas las cohortes/índices)
    //   use_study_units                -> "todos" (todos los RA micro, reporte 5)
    report_general_options() {
      return [
        {
          value: 3,
          icon: "grid-3x3-gap",
          use_matter: false,
          use_section: false,
          use_level: false,
          use_period: false,
          use_is_active_section: false,
          use_student_status: true,
          use_cohorts: true,
          use_cohort_index: true,
          use_study_units: false,
          text: this.$getVisibleNames(
            "manual.reporte.assessment_group_competence",
            false,
            this.$t("toolbar.reports.assessmentGroupCompetence", {
              competences: this.$getVisibleNames(
                "mesh.competence",
                true,
                "Competencias"
              ),
              egressProfile: this.$getVisibleNames(
                "mesh.egressprofile",
                false,
                "Perfil de egreso"
              ),
            })
          ),
        },
        {
          value: 2,
          icon: "diagram-3",
          use_matter: false,
          use_section: false,
          use_level: false,
          use_period: false,
          use_is_active_section: false,
          use_student_status: true,
          use_cohorts: true,
          use_cohort_index: true,
          use_study_units: false,
          text: this.$getVisibleNames(
            "manual.reporte.assessment_by_competence",
            false,
            this.$t("toolbar.reports.assessmentByCompetence", {
              competences: this.$getVisibleNames(
                "mesh.competence",
                true,
                "Competencias"
              ),
              egressProfile: this.$getVisibleNames(
                "mesh.egressprofile",
                false,
                "Perfil de egreso"
              ),
            })
          ),
        },
        {
          value: 1,
          icon: "journal-text",
          use_matter: true,
          use_section: true,
          use_level: true,
          use_period: true,
          use_is_active_section: false,
          use_student_status: true,
          use_cohorts: true,
          use_cohort_index: true,
          use_study_units: false,
          text: this.$getVisibleNames(
            "manual.reporte.assessment_by_matter",
            false,
            this.$t("toolbar.reports.assessmentByMatter", {
              matter: this.$getVisibleNames(
                "mesh.egressprofilematter",
                false,
                "Asignatura"
              ),
              egressProfile: this.$getVisibleNames(
                "mesh.egressprofile",
                false,
                "Perfil de egreso"
              ),
            })
          ),
        },
        {
          value: 4,
          icon: "bar-chart-line",
          use_matter: true,
          use_section: true,
          use_level: true,
          use_period: true,
          use_is_active_section: false,
          use_student_status: true,
          use_cohorts: true,
          use_cohort_index: true,
          use_study_units: false,
          text: this.$getVisibleNames(
            "manual.reporte.assessment_group_ra_micro",
            false,
            this.$t("toolbar.reports.assessmentGroupRaMicro", {
              raMicro: this.$getVisibleNames(
                "teaching.ramicro",
                false,
                "RA Micro"
              ),
              matter: this.$getVisibleNames(
                "mesh.egressprofilematter",
                false,
                "Asignatura"
              ),
            })
          ),
        },
        {
          value: 5,
          icon: "check2-square",
          use_matter: true,
          use_section: false,
          use_level: true,
          use_period: true,
          use_is_active_section: false,
          use_student_status: true,
          use_cohorts: false,
          use_cohort_index: false,
          use_study_units: true,
          text: this.$getVisibleNames(
            "manual.reporte.assessment_group_criteria",
            false,
            this.$t("toolbar.reports.assessmentGroupCriteria", {
              evaluationCriteria: this.$getVisibleNames(
                "manual.criterio_de_evaluacion",
                false,
                "Criterio de Evaluación"
              ),
              matter: this.$getVisibleNames(
                "mesh.egressprofilematter",
                false,
                "Asignatura"
              ),
            })
          ),
        },
        {
          value: 6,
          icon: "card-checklist",
          use_matter: false,
          use_section: false,
          use_level: false,
          use_period: false,
          use_is_active_section: false,
          use_student_status: true,
          use_cohorts: true,
          use_cohort_index: true,
          use_study_units: false,
          text: this.$getVisibleNames(
            "manual.reporte.average_grades",
            false,
            this.$t("toolbar.reports.averageGradesReport", {
              egressProfile: this.$getVisibleNames(
                "mesh.egressprofile",
                false,
                "Perfil de Egreso"
              ),
            })
          ),
        },
        {
          value: 8,
          icon: "person-lines-fill",
          use_matter: false,
          use_section: false,
          use_level: false,
          use_period: false,
          use_is_active_section: false,
          use_student_status: true,
          use_cohorts: true,
          use_cohort_index: true,
          use_study_units: false,
          text: this.$getVisibleNames(
            "manual.reporte.individual_grades",
            false,
            this.$t("toolbar.reports.reportIndividualGrades", {
              matter: this.$getVisibleNames(
                "mesh.egressprofilematter",
                false,
                "Asignatura"
              ),
            })
          ),
        },
        {
          value: 7,
          icon: "graph-up",
          use_matter: true,
          use_section: true,
          use_level: true,
          use_period: true,
          use_is_active_section: true,
          use_student_status: false,
          use_cohorts: false,
          use_cohort_index: false,
          use_study_units: false,
          text: this.$getVisibleNames(
            "manual.reporte.progression",
            false,
            this.$t("toolbar.reports.progressionReport", {
              evaluations: this.$getVisibleNames(
                "evaluations2.evaluation",
                true,
                "evaluaciones"
              ),
            })
          ),
        },
      ];
    },
  },
  methods: {
    // Lleva a GeneralReport con ese tipo de reporte y los filtros que ya
    // conocemos preseleccionados vía query params. Click navega en la misma
    // pestaña; click derecho abre una nueva.
    navigateToGeneralReport(option, new_tab = false) {
      const query = {
        report_type: String(option.value),
        egress_profiles: String(this.egress_profile_id),
      };
      if (option.use_matter)
        query.matters = String(this.egress_profile_matter_id);
      if (option.use_section) query.sections = String(this.section_id);
      // filteredLevels usa el número de nivel como id => plan_level de la asignatura
      if (option.use_level && this.plan_level != null)
        query.levels = String(this.plan_level);
      // El período se toma de la sección desde la que se navega
      if (option.use_period && this.period_id != null)
        query.periods = String(this.period_id);
      // is_active_section id 1 = períodos vigentes (secciones activas)
      if (option.use_is_active_section) query.is_active_section = "1";
      // Estado del estudiante: "todos" = en régimen (1) + no en régimen (2)
      if (option.use_student_status) query.student_status = "1,2";
      // Cohorte e índice de cohorte: "todos" (se resuelven al cargar el perfil)
      if (option.use_cohorts) query.cohorts = "all";
      if (option.use_cohort_index) query.cohort_index = "all";
      // RA micro / unidades de estudio: "todos" (se resuelven al cargar la asignatura)
      if (option.use_study_units) query.study_units = "all";
      const route = { name: "GeneralReport", query };
      if (new_tab) {
        const url = this.$router.resolve(route).href;
        window.open(url, "_blank");
      } else {
        this.$router.push(route);
      }
    },
  },
};
</script>

<style scoped>
.reports-nav-wrapper {
  display: inline-flex;
  align-items: center;
}
/* Trigger: botón discreto al lado de la sección/asignatura */
.reports-nav-trigger {
  padding: 2px 5px;
  border: 1px solid var(--kl-border-soft);
  border-radius: var(--kl-radius-md);
  color: var(--kl-color-primary);
}
.reports-nav-trigger:hover {
  background: var(--kl-surface-hover);
  border-color: var(--kl-color-accent-border);
}
/* Lista de reportes dentro del popover. Con los 8 reportes la lista se hace
   larga: se acota la altura y se hace scroll para que no se extienda tanto. */
.reports-nav-popover {
  display: flex;
  flex-direction: column;
  gap: 6px;
  min-width: 220px;
  max-width: 260px;
  max-height: 260px;
  overflow-y: auto;
  /* Espacio para que la barra de scroll no tape el borde de los botones */
  padding-right: 4px;
}
.reports-nav-btn {
  width: 100%;
  /* Compactos pero con texto completo: los nombres largos envuelven en varias
     líneas sin cortarse. */
  min-height: 30px;
  height: auto;
  flex-shrink: 0;
  border-radius: 4px;
  padding: 5px 8px;
  font-size: var(--kl-fs-caption, 0.8125rem);
  text-align: left;
  white-space: normal;
  line-height: 1.2;
  transition: background-color 0.3s, color 0.3s, box-shadow 0.3s;
}
</style>
